import dayjs, { Dayjs } from "dayjs";
import { useMemo } from "react";

import { LineChart } from "@design-system/LineChart/LineChart";

import { useResourceGroupEmployeeUsageLineChartQuery } from "./ResourceGroupEmployeeUsageLineChart.g";

type Props = {
  clientApplicationId: string;
  daysToShow?: number;
  startDate?: Date;
  endDate?: Date;
};

const formatQueryDate = (date: Date | Dayjs): string =>
  dayjs(date).format("YYYY-MM-DD");

const resolveQueryBounds = (
  startDate: Date | undefined,
  endDate: Date | undefined,
  daysToShow: number,
): { dateFrom?: string; dateTo: string; openStart: boolean } => {
  if (startDate && endDate) {
    return {
      dateFrom: formatQueryDate(startDate),
      dateTo: formatQueryDate(endDate),
      openStart: false,
    };
  }
  if (startDate) {
    return {
      dateFrom: formatQueryDate(startDate),
      dateTo: formatQueryDate(dayjs()),
      openStart: false,
    };
  }
  if (endDate) {
    return {
      dateTo: formatQueryDate(endDate),
      openStart: true,
    };
  }
  const end = dayjs();
  return {
    dateFrom: formatQueryDate(end.subtract(daysToShow, "day")),
    dateTo: formatQueryDate(end),
    openStart: false,
  };
};

const buildAxisDays = (start: Dayjs, end: Dayjs): Dayjs[] => {
  const dayCount = Math.max(1, end.diff(start, "day") + 1);
  return Array.from({ length: dayCount }, (_, i) =>
    start.add(i, "day"),
  );
};

export const ResourceGroupEmployeeUsageLineChart = ({
  clientApplicationId,
  daysToShow = 30,
  startDate,
  endDate,
}: Props) => {
  const queryBounds = useMemo(
    () => resolveQueryBounds(startDate, endDate, daysToShow),
    [startDate, endDate, daysToShow],
  );

  const { data } = useResourceGroupEmployeeUsageLineChartQuery({
    clientApplicationId,
    dateFrom: queryBounds.dateFrom,
    dateTo: queryBounds.dateTo,
  });

  const chartData = useMemo(() => {
    if (!data?.resourceGroupUsage) {
      return { chartData: [], weekendDates: [] };
    }

    const usageByDate = new Map<string, number>();
    data.resourceGroupUsage.forEach((usage) => {
      if (usage?.date && usage.employeeCount !== undefined) {
        usageByDate.set(
          dayjs(usage.date).format("YYYY-MM-DD"),
          usage.employeeCount,
        );
      }
    });

    let axisStart: Dayjs | null;
    const axisEnd = dayjs(queryBounds.dateTo);

    if (queryBounds.openStart) {
      const dates = Array.from(usageByDate.keys());
      if (dates.length === 0) {
        return { chartData: [], weekendDates: [] };
      }
      axisStart = dates.reduce((earliest, isoDate) => {
        const current = dayjs(isoDate);
        return current.isBefore(earliest) ? current : earliest;
      }, dayjs(dates[0]));
    } else {
      axisStart = dayjs(queryBounds.dateFrom);
    }

    const days = buildAxisDays(axisStart, axisEnd);
    const points = days.map((currentDate) => {
      const dateStr = currentDate.format("YYYY-MM-DD");
      return {
        x: currentDate.format("DD/MM/YYYY"),
        y: usageByDate.get(dateStr) || 0,
      };
    });

    const weekendDates = days
      .filter((currentDate) => currentDate.day() === 0 || currentDate.day() === 6)
      .map((currentDate) => currentDate.format("DD/MM/YYYY"));

    return { chartData: points, weekendDates };
  }, [data, queryBounds]);

  const chartKey = useMemo(
    () =>
      `${queryBounds.dateFrom ?? "open"}-${queryBounds.dateTo}-${clientApplicationId}`,
    [queryBounds, clientApplicationId],
  );

  return (
    <LineChart
      key={chartKey}
      data={
        chartData.chartData.length === 0
          ? []
          : [
              {
                id: "user-usage",
                color: "red",
                data: chartData.chartData,
              },
            ]
      }
      weekendDates={chartData.weekendDates}
      xAxisName=""
      yAxisName="User count"
      tooltipLabel="Users"
    />
  );
};
